// @flow

import React, { PureComponent } from 'react';
import qs from 'qs';
import debounce from 'lodash.debounce';
import { Map } from 'immutable';
import { graphql, QueryRenderer } from 'react-relay';
import { i18n, Analytics } from 'shared/utils';
import relayEnvironment from 'shared/gql/relayEnvironment';
import Loader from 'shared/components/common/Loader';
import Page from 'main-app/components/Page';
import PageHeaderList from 'main-app/components/PageHeaderList';
import AddUpdateShipmentModal from 'main-app/components/AddUpdateShipmentModal';
import FilterControls, { type FilterValue } from 'main-app/components/FilterControls';
import type { RouterHistory } from 'react-router';
import getSortOptions from './getSortOptions';
import getFilterOptions from './getFilterOptions';
import PaginatedShipmentsContainer from './PaginatedShipmentsContainer';

type Props = { history: RouterHistory };

type State = {
  addModalOpen: boolean,
  search: string,
  graphqlSearchValue: string,
  createAnotherShipment: boolean,
};

const DEFAULT_FILTER = {
  sort: 'CREATED_AT',
  direction: 'DESC',
};

class Shipments extends PureComponent<Props, State> {
  constructor(props: Props) {
    super(props);
    this.state = {
      addModalOpen: false,
      search: '',
      graphqlSearchValue: '',
      createAnotherShipment: false,
    };
    this.handleDebouncedSearch = debounce(this.handleDebouncedSearch, 500);
  }

  getFilters = (): FilterValue => {
    const { history } = this.props;
    const { search } = history.location;
    if (!search) return DEFAULT_FILTER;
    return { ...DEFAULT_FILTER, ...qs.parse(search, { ignoreQueryPrefix: true }) };
  };

  setFilters = (filters: Object) => {
    const { history } = this.props;
    history.replace(
      '/shipments?' + qs.stringify(filters, { encode: true, arrayFormat: 'brackets' }),
    );
  };

  filtersDirty = () => {
    const { graphqlSearchValue } = this.state;
    return !Map(this.getFilters()).equals(Map(DEFAULT_FILTER)) || graphqlSearchValue;
  };

  compileQueryVariables = () => {
    const filters = this.getFilters();
    const { graphqlSearchValue } = this.state;
    const queryVariables = {
      ...filters,
      search: graphqlSearchValue.trim() || null,
      sortBy: { field: filters.sort, direction: filters.direction },
    };

    if (queryVariables.estimatedShipDate) {
      const [start, end] = queryVariables.estimatedShipDate.split(' - ');
      queryVariables.estimatedShipDateStart = start;
      queryVariables.estimatedShipDateEnd = end;
      delete queryVariables.estimatedShipDate;
    }

    if (queryVariables.actualShipDate) {
      const [start, end] = queryVariables.actualShipDate.split(' - ');
      queryVariables.actualShipDateStart = start;
      queryVariables.actualShipDateEnd = end;
      delete queryVariables.actualShipDate;
    }

    delete queryVariables.sort;
    delete queryVariables.direction;
    return queryVariables;
  };

  handleSearchInput = (search: string) => {
    this.setState({ search }, this.handleDebouncedSearch);
  };

  handleDebouncedSearch = () => {
    const { search } = this.state;
    this.setState({ graphqlSearchValue: search });
    Analytics.trackEvent('Search Applied', {
      listName: 'Shipments',
      searchString: search,
    });
  };

  handleOpenCreateModal = () => {
    this.setState({ addModalOpen: true });
  };

  handleCloseAllModals = () => {
    this.setState({ addModalOpen: false, createAnotherShipment: false });
  };

  handleAddModalReopen = () => {
    this.setState(
      { addModalOpen: false, createAnotherShipment: true },
      () => {
        this.setState({ addModalOpen: true });
      },
    );
  };

  render() {
    const { history } = this.props;
    const { search, addModalOpen, createAnotherShipment } = this.state;

    return (
      <QueryRenderer
        environment={relayEnvironment}
        cacheConfig={{ force: true }}
        query={graphql`
          query ShipmentsQuery(
            $count: Int!
            $cursor: String
            $search: String
            $sortBy: ShipmentSort
            $statuses: [ShipmentStatus!]
            $customerIds: [ID!]
            $orderIds: [ID!]
            $estimatedShipDateStart: Date
            $estimatedShipDateEnd: Date
            $actualShipDateStart: Date
            $actualShipDateEnd: Date
          ) {
            ...PaginatedShipmentsContainer_shipments
              @arguments(
                search: $search
                sortBy: $sortBy
                statuses: $statuses
                customerIds: $customerIds
                orderIds: $orderIds
                estimatedShipDateStart: $estimatedShipDateStart
                estimatedShipDateEnd: $estimatedShipDateEnd
                actualShipDateStart: $actualShipDateStart
                actualShipDateEnd: $actualShipDateEnd
              )
            viewer {
              id
              permissions
              company {
                id
              }
            }
          }
        `}
        variables={{ ...this.compileQueryVariables(), count: 30, cursor: null }}
        render={({ props }) => (
          <Page>
            <PageHeaderList
              title={i18n.t('Shipments')}
              createButtonLabel={
                props &&
                props.viewer.permissions.includes('CREATE_SHIPMENT') &&
                i18n.t('New Shipment')
              }
              onCreateButtonClick={this.handleOpenCreateModal}
              searchPlaceholder={i18n.t('Customer, Order #, Tracking #, or Shipment #')}
              searchValue={search}
              onSearchChange={this.handleSearchInput}
            />
            <FilterControls
              value={this.getFilters()}
              defaultValue={DEFAULT_FILTER}
              sortOptions={getSortOptions()}
              filterOptions={getFilterOptions()}
              savedFilterType="SHIPMENT"
              onFiltersChange={filters => this.setFilters(filters)}
            />
            {!props ? (
              <Loader />
            ) : (
              <PaginatedShipmentsContainer
                history={history}
                filtersDirty={this.filtersDirty()}
                onCreateNew={this.handleOpenCreateModal}
                shipments={props}
                deleteEnabled={props.viewer.permissions.includes('DELETE_SHIPMENT')}
                editEnabled={props.viewer.permissions.includes('UPDATE_SHIPMENT')}
              />
            )}
            {addModalOpen && (
              <AddUpdateShipmentModal
                onClose={this.handleCloseAllModals}
                onSuccess={shipment => history.push(`/shipment/${shipment.id}`)}
                onReopen={this.handleAddModalReopen}
                createAnotherShipment={createAnotherShipment}
                shipment={null}
              />
            )}
          </Page>
        )}
      />
    );
  }
}

export default Shipments;
